import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { NotificationService } from 'src/core/services/notification.service';

export interface NotificationItem {
  id: string;
  recipientMappingId: number | string;
  title: string;
  message: string;
  time: string;
  type: 'info' | 'success' | 'warning' | 'danger';
  icon: string;
  read: boolean;
  rawData?: any;
}

@Component({
  selector: 'app-notification-offcanvas',
  standalone: true,
  imports: [CommonModule, InfiniteScrollDirective],
  templateUrl: './notification-offcanvas.html',
})
export class NotificationOffcanvas implements OnInit {
  activeOffcanvas = inject(NgbActiveOffcanvas);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  notifications: NotificationItem[] = [];
  pageNumber: number = 1;
  pageSize: number = 10;
  isLoading: boolean = false;
  hasMore: boolean = true;

  ngOnInit(): void {
    this.fetchUnreadCount();
    this.fetchNotifications(true);
  }

  fetchUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe({
      next: () => {
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  fetchNotifications(isInitial: boolean = true): void {
    if (this.isLoading) return;
    if (!isInitial && !this.hasMore) return;

    if (isInitial) {
      this.pageNumber = 1;
      this.hasMore = true;
      this.notifications = [];
    }

    this.isLoading = true;

    const params = new HttpParams()
      .set('pageSize', String(this.pageSize))
      .set('pageNumber', String(this.pageNumber))
      .set('orderByCol', 'recipient_mapping_id')
      .set('orderByDir', 'desc');

    this.notificationService.getMyNotifications(params).subscribe({
      next: (res: any) => {
        const rawItems =
          res?.data?.content ||
          res?.content ||
          (Array.isArray(res?.data) ? res.data : []);

        const mappedItems = rawItems.map((item: any) =>
          this.mapNotificationItem(item),
        );

        if (isInitial) {
          this.notifications = mappedItems;
        } else {
          this.notifications = [...this.notifications, ...mappedItems];
        }

        if (rawItems.length < this.pageSize || res?.data?.last === true) {
          this.hasMore = false;
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onScroll(): void {
    if (!this.isLoading && this.hasMore) {
      this.pageNumber++;
      this.fetchNotifications(false);
    }
  }

  onNotificationClick(notification: NotificationItem): void {
    if (!notification.read && notification.recipientMappingId) {
      this.notificationService
        .markAsRead(notification.recipientMappingId)
        .subscribe();
      notification.read = true;
      this.fetchUnreadCount();
    }
    this.activeOffcanvas.close();
    this.notificationService.navigateToNotification(
      notification.rawData || notification,
    );
  }

  private mapNotificationItem(item: any): NotificationItem {
    const mappingId = item.recipient_mapping_id ?? item.id;
    return {
      id: String(mappingId ?? Math.random()),
      recipientMappingId: mappingId,
      title: item.subject ?? item.title ?? 'Notification',
      message: item.in_app_body ?? item.message ?? item.description ?? '',
      time: this.formatTime(item.notified_at ?? item.created_at ?? item.time),
      type: this.normalizeType(item.event_key ?? item.type),
      icon: item.icon ?? this.getIconForType(item.event_key ?? item.type),
      read: Boolean(item.is_read ?? item.read ?? false),
      rawData: {
        eventKey: item.event_key ?? item.eventKey,
        entityType: item.entity_type ?? item.entityType,
        entityId: item.entity_id ?? item.entityId,
        route: item.route,
        url: item.url,
      },
    };
  }

  private formatTime(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} mins ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return d.toLocaleDateString();
    } catch {
      return dateStr || '';
    }
  }

  private normalizeType(
    type?: string,
  ): 'info' | 'success' | 'warning' | 'danger' {
    if (!type) return 'info';
    const lower = type.toLowerCase();
    if (lower.includes('success')) return 'success';
    if (lower.includes('warn')) return 'warning';
    if (lower.includes('danger') || lower.includes('error')) return 'danger';
    return 'info';
  }

  private getIconForType(type?: string): string {
    const norm = this.normalizeType(type);
    switch (norm) {
      case 'success':
        return 'ph-check-circle';
      case 'warning':
        return 'ph-shield-warning';
      case 'danger':
        return 'ph-warning-octagon';
      case 'info':
      default:
        return 'ph-bell';
    }
  }

  get unreadCount(): number {
    return this.notificationService.unreadCount();
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications.forEach((n) => (n.read = true));
        this.fetchUnreadCount();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        console.error('Error marking all notifications as read:', err);
      },
    });
  }

  toggleReadStatus(notification: NotificationItem, event: MouseEvent): void {
    event.stopPropagation();
    if (!notification.read && notification.recipientMappingId) {
      this.notificationService
        .markAsRead(notification.recipientMappingId)
        .subscribe({
          next: () => {
            notification.read = true;
            this.fetchUnreadCount();
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            console.error('Error marking notification as read:', err);
          },
        });
    }
  }
}
